import React, { useCallback, useRef, useState } from 'react';
import { Icons } from '../../../components/atoms/SVG';
import Btn, { BtnModifier } from '../../../components/molecules/Btn';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { focusOnReturn } from '../../../lib/DomUtils/utils';
import { AcceptDeclineForm } from '../../../_lib/Form2/standardForms/AcceptDeclineForm/AcceptDeclineForm';
import { useDelayed } from '../../../hooks/useDelayed';
import { PageTypeIcon } from '../../../_lib/components/PageType';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { useInvalidateRefreshableBlock, useRefreshBlock } from '../../../_lib/BlockFetcher';
import { PrivilegePageType } from '../PrivilegeConfig.types';
import { BlockType } from '../../../types/api/ElementType';
import { Hint } from '../../../_lib/components/Hint';
import { translatePageTypeWithLegacyTitle } from '../../../_lib/components/PageType/PageTypeTitle';
import { PageType } from '../../../types/api/PageType';

type BasicPrivilegeTicketProps = {
  privilegePageType: PrivilegePageType;
};

export function BasicPrivilegeTicket({ privilegePageType }: BasicPrivilegeTicketProps) {
  const { t } = useSonTranslation('PrivilegeConfig');
  const ticketRef = useRef<HTMLDivElement>(null);

  const invalidateRefreshableBlock = useInvalidateRefreshableBlock();

  const [showModal, setShowModal] = useState(false);

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const { refresh } = useRefreshBlock();

  const refreshAndClose = useCallback(() => {
    refresh();
    // we also want to refresh the additional privileges block.
    invalidateRefreshableBlock(BlockType.additionalprivilegeconfig);
    closeModal();
  }, [closeModal, refresh, invalidateRefreshableBlock]);
  const delayedRefreshAndClose = useDelayed(refreshAndClose);

  const pageTypeTitle = translatePageTypeWithLegacyTitle(privilegePageType.pageType as PageType);
  const deletePageTypeTitle = t('deletePageType', { name: pageTypeTitle });

  return (
    <>
      <div className="privilege-config__basic-privilege-ticket" ref={ticketRef}>
        <div className="privilege-config__basic-privilege-ticket-label">
          <PageTypeIcon pageType={privilegePageType.pageType} />
          <p>
            {pageTypeTitle}
          </p>
        </div>
        <div className="privilege-config__privilege-ticket-button">
          <Btn
            modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
            svgSpriteId={Icons.DELETE_X_SMALL}
            type="button"
            text={deletePageTypeTitle}
            titleText={deletePageTypeTitle}
            hiddenText
            tooltipPlacement={TooltipPlacement.TOP}
            onClick={() => {
              setShowModal(true);
            }}
          />
        </div>
      </div>
      {showModal && (
        <ModalDialog
          closeModal={closeModal}
          headline={deletePageTypeTitle}
          initialFocusIndex={1}
          focusOnReturn={focusOnReturn(ticketRef.current?.parentElement)}
        >
          <AcceptDeclineForm
            headline={t('deletePageTypeQuestion')}
            form={privilegePageType.actions.deletePageType}
            messageSuccess={t('deleteStatus.success')}
            messageSending={t('deleteStatus.sending')}
            messageFail={t('deleteStatus.fail')}
            onSuccess={delayedRefreshAndClose}
          >
            <Hint message={t('deleteFormHint')} />
          </AcceptDeclineForm>
        </ModalDialog>
      )}
    </>
  );
}
